import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Camera, Loader2, ChevronDown, Check, Copy, ArrowLeft,
  ShieldCheck, AlertTriangle, XCircle, X,
} from "lucide-react";
import SignaturePad from "../shared/SignaturePad";
import { uploadFiles } from "../shared/cloudinaryUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OVERALL = [
  { key: "approved", label: "Godkjent", color: "bg-emerald-500", icon: ShieldCheck },
  { key: "conditional", label: "Godkjent m/anmerkning", color: "bg-amber-500", icon: AlertTriangle },
  { key: "rejected", label: "Ikke godkjent", color: "bg-red-500", icon: XCircle },
];

const STATUS = [
  { key: "ok", label: "OK", color: "bg-emerald-500 text-white" },
  { key: "warning", label: "Merknad", color: "bg-amber-500 text-white" },
  { key: "fail", label: "Avvist", color: "bg-red-500 text-white" },
];

const emptyScaffold = () => ({
  name: "", height: "", length: "", width: "", capacity: "", number_of_anchors: "",
  photos: [], signed_card_photo: null, notes: "", status: "ok",
});

const emptyForm = () => ({
  language: "en",
  project_id: null,
  address: "", customer: "", customer_email: "",
  inspection_date: new Date().toISOString().slice(0, 10),
  inspector_name: "", inspector_code: "",
  weather_conditions: "",
  general_notes: "", overall_status: "approved",
  scaffolds: [emptyScaffold()],
  inspector_signature: null,
});

/** Searchable project dropdown — auto-fills address/customer/email. */
function ProjectSelect({ projects, onPick, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return projects;
    return projects.filter((p) => [p.address, p.customer, p.customer_email, p.name].some((f) => (f || "").toLowerCase().includes(s)));
  }, [q, projects]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white border border-cyan-300 rounded-md text-left text-sm hover:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        data-testid="select-project">
        <span className="text-slate-500">{placeholder}</span>
        <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-300 rounded-md shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Søk..."
              className="w-full px-2 py-1.5 text-sm text-slate-900 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500" />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && <div className="px-3 py-3 text-sm text-slate-400 italic">Ingen treff</div>}
            {filtered.map((p) => (
              <button type="button" key={p.id}
                onClick={() => { onPick(p); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-2 text-sm text-slate-900 hover:bg-cyan-50 border-b border-slate-50 last:border-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{p.address}</span>
                  {p.name && <span className="text-[10px] bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded font-mono">{p.name}</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{p.customer}{p.customer_email ? ` — ${p.customer_email}` : ""}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Reusable photo upload chip row */
function PhotoRow({ photos, onAdd, onRemove, onPreview, uploading, testidPrefix, label, onPickProgress }) {
  const inputRef = useRef(null);
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
        <div className="flex items-center gap-1">
          {onPickProgress && (
            <button type="button" onClick={onPickProgress} disabled={uploading}
              className="flex items-center gap-1 px-2 py-1 bg-cyan-600 text-white text-xs rounded hover:bg-cyan-700 disabled:opacity-50" style={{ color: "#fff" }}
              data-testid={`${testidPrefix}-from-progress`}>
              <Camera className="w-3 h-3" /> Fra progresjon
            </button>
          )}
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="flex items-center gap-1 px-2 py-1 bg-slate-900 text-white text-xs rounded hover:bg-slate-800 disabled:opacity-50" style={{ color: "#fff" }}>
            <Camera className="w-3 h-3" /> {uploading ? "..." : "Last opp"}
          </button>
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => onAdd(e.target.files)} />
      {photos && photos.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {photos.map((u, i) => (
            <div key={i} className="relative w-20 h-20 rounded border border-slate-200 overflow-hidden group" data-testid={`${testidPrefix}-${i}`}>
              <img src={u} alt="" className="w-full h-full object-cover cursor-pointer" onClick={() => onPreview(u)} />
              <button type="button" onClick={() => onRemove(i)}
                className="absolute top-0 right-0 p-0.5 bg-red-500 text-white rounded-bl opacity-90 hover:opacity-100" style={{ color: "#fff" }}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      ) : <p className="text-xs text-slate-400 italic">Ingen bilder</p>}
    </div>
  );
}

export default function KontrollForm() {
  const navigate = useNavigate();
  const { editToken } = useParams();
  const [searchParams] = useSearchParams();
  const prefilledProjectId = searchParams.get("project_id");
  const [form, setForm] = useState(emptyForm);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState({ edit: false, sign: false });
  const [lightbox, setLightbox] = useState(null);
  // Photos picked FROM the progress feed — excluded when we post PC photos back to it.
  const progressPickedRef = useRef(new Set());
  const [progress, setProgress] = useState({ open: false, loading: false, idx: null, photos: [], sel: new Set() });
  const [importer, setImporter] = useState({ open: false, loading: false, q: "", items: [] });
  const readOnly = form.status === "signed";

  // Load projects + optional draft
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/underlag/projects`);
        const list = data.projects || [];
        setProjects(list);
        // Pre-select project when deep-linked from a Doserne project card.
        if (prefilledProjectId && !editToken) {
          const p = list.find((x) => x.id === prefilledProjectId);
          if (p) {
            setForm((f) => ({
              ...f,
              project_id: p.id,
              address: p.address || "",
              customer: p.customer || "",
              customer_email: p.customer_email || "",
            }));
          }
        }
      } catch { /* silent */ }
    })();
    if (editToken) {
      (async () => {
        try {
          const { data } = await axios.get(`${API}/kontroll/edit/${editToken}`);
          setForm({ ...emptyForm(), ...data, scaffolds: data.scaffolds?.length ? data.scaffolds : [emptyScaffold()] });
        } catch (e) {
          toast.error("Kunne ikke laste kontroll");
        }
      })();
    }
  }, [editToken, prefilledProjectId]);

  const updateScaffold = (idx, patch) => setForm((f) => ({ ...f, scaffolds: f.scaffolds.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }));
  const addScaffold = () => setForm((f) => ({ ...f, scaffolds: [...f.scaffolds, emptyScaffold()] }));
  const removeScaffold = (i) => setForm((f) => ({ ...f, scaffolds: f.scaffolds.filter((_, ix) => ix !== i) }));

  const uploadPhotosFor = async (idx, files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = await uploadFiles(files, "uploads/kontroll", API, axios);
      updateScaffold(idx, { photos: [...form.scaffolds[idx].photos, ...uploaded.map((u) => u.url)] });
    } catch (e) { toast.error(e.message); }
    setUploading(false);
  };
  const uploadCardFor = async (idx, files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const [u] = await uploadFiles([files[0]], "uploads/kontroll/cards", API, axios);
      updateScaffold(idx, { signed_card_photo: u.url });
    } catch (e) { toast.error(e.message); }
    setUploading(false);
  };
  const removePhotoAt = (idx, pi) => updateScaffold(idx, { photos: form.scaffolds[idx].photos.filter((_, x) => x !== pi) });

  // ── Pick photos from the project's daily progress feed (like Underlag) ──
  const openProgressPicker = async (idx) => {
    if (!form.project_id) { toast.error("Velg prosjekt først"); return; }
    setProgress({ open: true, loading: true, idx, photos: [], sel: new Set() });
    try {
      const { data } = await axios.get(`${API}/underlag/project-progress-photos`, { params: { project_id: form.project_id } });
      setProgress((p) => ({ ...p, loading: false, photos: data.photos || [] }));
    } catch {
      toast.error("Kunne ikke hente bilder");
      setProgress((p) => ({ ...p, loading: false, photos: [] }));
    }
  };
  const toggleProgressSel = (url) => setProgress((p) => {
    const sel = new Set(p.sel);
    if (sel.has(url)) sel.delete(url); else sel.add(url);
    return { ...p, sel };
  });
  const addProgressSelected = () => {
    const { idx, sel, photos } = progress;
    const existing = new Set(form.scaffolds[idx]?.photos || []);
    const toAdd = photos.filter((ph) => sel.has(ph.url) && !existing.has(ph.url)).map((ph) => ph.url);
    toAdd.forEach((u) => progressPickedRef.current.add(u));
    updateScaffold(idx, { photos: [...(form.scaffolds[idx]?.photos || []), ...toAdd] });
    setProgress({ open: false, loading: false, idx: null, photos: [], sel: new Set() });
    if (toAdd.length) toast.success(`+${toAdd.length}`);
  };

  // ── Import scaffolds from a previous kontroll (card photos cleared for re-take) ──
  const openImporter = async () => {
    setImporter({ open: true, loading: true, q: "", items: [] });
    try {
      const { data } = await axios.get(`${API}/kontroll/import-list`, { params: { project_id: form.project_id || "" } });
      setImporter((s) => ({ ...s, loading: false, items: data.items || [] }));
    } catch {
      toast.error("Kunne ikke hente kontroller");
      setImporter((s) => ({ ...s, loading: false, items: [] }));
    }
  };
  const applyImport = (item) => {
    const scaffolds = (item.scaffolds || []).map((s) => ({
      ...emptyScaffold(),
      ...s,
      height: s.height ?? "", length: s.length ?? "", width: s.width ?? "",
      capacity: s.capacity ?? "", number_of_anchors: s.number_of_anchors ?? "",
      photos: [...(s.photos || [])],   // scaffold photos stay
      signed_card_photo: null,          // card photos re-taken for the new inspection
    }));
    setForm((f) => ({
      ...f,
      address: f.address || item.address || "",
      customer: f.customer || item.customer || "",
      customer_email: f.customer_email || item.customer_email || "",
      project_id: f.project_id || item.project_id || null,
      scaffolds: scaffolds.length ? scaffolds : [emptyScaffold()],
    }));
    setImporter({ open: false, loading: false, q: "", items: [] });
    toast.success(`Importert fra ${item.code}`);
  };

  const canSave = () =>
    form.inspection_date && form.inspector_name.trim() &&
    form.scaffolds.length > 0 && form.scaffolds.every((s) => s.name.trim() || s.height || s.length);

  const save = async () => {
    if (!canSave()) { toast.error("Fyll ut inspektør + minst ett stillas"); return; }
    setSaving(true);
    try {
      // PC-uploaded photos (not the ones picked from the progress feed) get
      // auto-posted to the project's daily progress on the inspection date.
      const picked = progressPickedRef.current;
      const pcPhotos = [];
      form.scaffolds.forEach((s) => (s.photos || []).forEach((u) => {
        if (u && !picked.has(u) && !pcPhotos.includes(u)) pcPhotos.push(u);
      }));
      const payload = {
        ...form,
        progress_post_photos: pcPhotos,
        scaffolds: form.scaffolds.map((s) => ({
          ...s,
          height: parseFloat(s.height) || null,
          length: parseFloat(s.length) || null,
          width: parseFloat(s.width) || null,
          capacity: parseFloat(s.capacity) || null,
          number_of_anchors: parseInt(s.number_of_anchors, 10) || null,
        })),
      };
      if (editToken) {
        await axios.put(`${API}/kontroll/edit/${editToken}`, payload);
        toast.success("Oppdatert");
      } else {
        const { data } = await axios.post(`${API}/kontroll/create`, payload);
        setResult(data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Feil ved lagring"); }
    setSaving(false);
  };

  const copyLink = (url, kind) => {
    navigator.clipboard.writeText(url);
    setCopied({ ...copied, [kind]: true });
    setTimeout(() => setCopied({ edit: false, sign: false }), 2000);
  };

  return (
    <div className="light-theme min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pb-16 text-slate-900">
      {/* Sticky header */}
      <div className="sticky top-0 z-40 bg-slate-900 text-white px-4 py-3 flex items-center gap-3 shadow-md">
        <button onClick={() => navigate("/doserne")} className="p-1 hover:bg-white/10 rounded" data-testid="back-btn" style={{ color: "#fff" }} title="Tilbake til Doserne">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">Doserne Stillas AS</div>
          <h1 className="text-base font-bold" style={{ color: "#fff" }}>Kontrollrapport</h1>
        </div>
        <button onClick={() => navigate("/doserne")}
          className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest bg-slate-800 hover:bg-slate-700 border border-slate-700 whitespace-nowrap"
          style={{ color: "#fff" }} data-testid="back-to-doserne"
        >← Doserne</button>
      </div>

      {readOnly && (
        <div className="max-w-4xl mx-auto px-4 pt-4" data-testid="kontroll-locked-banner">
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-lg px-4 py-3 text-sm font-semibold">
            <ShieldCheck className="w-4 h-4 flex-shrink-0" />
            Rapporten er signert av kunden og kan ikke endres.
          </div>
        </div>
      )}
      <fieldset disabled={readOnly} className="max-w-4xl mx-auto p-4 space-y-4 border-0 m-0 min-w-0 disabled:opacity-60">
        {/* Step 1: Project selector — always visible at top */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-cyan-300 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-cyan-500 text-white flex items-center justify-center font-black text-sm shadow-sm" style={{ color: "#fff" }}>1</div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-bold text-slate-900 mb-1">Velg prosjekt</label>
              {projects.length > 0 ? (
                <>
                  <ProjectSelect projects={projects} placeholder="— Søk eller velg prosjekt —"
                    onPick={(p) => { setForm((f) => ({ ...f, project_id: p.id, address: p.address, customer: p.customer, customer_email: p.customer_email })); toast.success(p.address); }} />
                  <p className="text-xs text-slate-500 mt-1">Adresse, Kunde og Kundens e-post fylles ut automatisk. Eller fyll ut manuelt under.</p>
                </>
              ) : (
                <p className="text-sm text-slate-500 italic">Ingen prosjekter i katalogen ennå. Fyll ut manuelt under, eller <a href="/admin/underlag" className="text-cyan-600 underline">legg til prosjekter fra admin</a>.</p>
              )}
              {form.project_id && (
                <div className="mt-2 flex items-center gap-2 px-2 py-1 bg-cyan-50 border border-cyan-200 rounded text-xs">
                  <span className="text-cyan-700 font-semibold">✓ {form.address}</span>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, project_id: null }))}
                    className="ml-auto text-cyan-600 hover:text-cyan-900 text-xs" data-testid="clear-project-btn">Bytt</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 1: Basic */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Inspeksjonsdato *</label>
              <input type="date" value={form.inspection_date} onChange={(e) => setForm({ ...form, inspection_date: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900" data-testid="input-date" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Adresse / Prosjekt</label>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Gateadresse" className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="input-address" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Kunde</label>
              <input value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })}
                placeholder="Firma" className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="input-customer" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Kundens e-post</label>
              <input value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                placeholder="klient@firma.no" className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="input-customer-email" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Inspektør *</label>
              <input value={form.inspector_name} onChange={(e) => setForm({ ...form, inspector_name: e.target.value })}
                placeholder="Navn" className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="input-inspector" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Kompetansekode</label>
              <input value={form.inspector_code} onChange={(e) => setForm({ ...form, inspector_code: e.target.value })}
                placeholder="f.eks. Klasse 2" className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="input-inspector-code" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-slate-700 mb-1">Værforhold</label>
              <input value={form.weather_conditions} onChange={(e) => setForm({ ...form, weather_conditions: e.target.value })}
                placeholder="f.eks. Solrik, 12°C, vind 5m/s" className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Overordnet status *</label>
            <div className="grid grid-cols-3 gap-2">
              {OVERALL.map((o) => {
                const active = form.overall_status === o.key;
                const Icon = o.icon;
                return (
                  <button type="button" key={o.key} onClick={() => setForm({ ...form, overall_status: o.key })}
                    className={`flex flex-col items-center gap-1 py-3 rounded-lg border-2 transition ${active ? `${o.color} border-transparent shadow` : "bg-white border-slate-200 hover:border-slate-300"}`}
                    style={active ? { color: "#fff" } : {}} data-testid={`overall-${o.key}`}>
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-semibold text-center px-1">{o.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Section 2: Scaffolds */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-slate-900">Stillaser</h2>
            <button type="button" onClick={openImporter}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-cyan-400 text-cyan-700 text-xs font-bold rounded-md hover:bg-cyan-50"
              data-testid="import-kontroll-btn">
              <Copy className="w-3.5 h-3.5" /> Importer fra tidligere kontroll
            </button>
          </div>
          {form.scaffolds.map((s, idx) => (
            <div key={idx} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50" data-testid={`scaffold-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500 uppercase">#{idx + 1}</span>
                <button type="button" onClick={() => removeScaffold(idx)}
                  className="text-red-500 hover:bg-red-50 p-1 rounded" data-testid={`remove-scaffold-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <input value={s.name} onChange={(e) => updateScaffold(idx, { name: e.target.value })}
                placeholder="Stillas navn / lokasjon" className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded text-slate-900 placeholder:text-slate-400" />
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                <input type="number" step="0.1" value={s.height} onChange={(e) => updateScaffold(idx, { height: e.target.value })} placeholder="H (m)" className="px-2 py-1.5 text-sm border border-slate-300 rounded text-slate-900 placeholder:text-slate-400" />
                <input type="number" step="0.1" value={s.length} onChange={(e) => updateScaffold(idx, { length: e.target.value })} placeholder="L (m)" className="px-2 py-1.5 text-sm border border-slate-300 rounded text-slate-900 placeholder:text-slate-400" />
                <input type="number" step="0.1" value={s.width} onChange={(e) => updateScaffold(idx, { width: e.target.value })} placeholder="B (m)" className="px-2 py-1.5 text-sm border border-slate-300 rounded text-slate-900 placeholder:text-slate-400" />
                <input type="number" value={s.capacity} onChange={(e) => updateScaffold(idx, { capacity: e.target.value })} placeholder="kg/m²" className="px-2 py-1.5 text-sm border border-slate-300 rounded text-slate-900 placeholder:text-slate-400" />
                <input type="number" value={s.number_of_anchors} onChange={(e) => updateScaffold(idx, { number_of_anchors: e.target.value })} placeholder="Forankr." className="px-2 py-1.5 text-sm border border-slate-300 rounded text-slate-900 placeholder:text-slate-400" />
              </div>
              <textarea value={s.notes} onChange={(e) => updateScaffold(idx, { notes: e.target.value })} placeholder="Merknader"
                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded text-slate-900 placeholder:text-slate-400" rows={2} />
              <div className="flex gap-1">
                {STATUS.map((st) => (
                  <button type="button" key={st.key} onClick={() => updateScaffold(idx, { status: st.key })}
                    className={`px-3 py-1 text-xs font-semibold rounded ${s.status === st.key ? st.color : "bg-white border border-slate-300 text-slate-600"}`}
                    style={s.status === st.key ? { color: "#fff" } : {}}>
                    {st.label}
                  </button>
                ))}
              </div>
              <PhotoRow photos={s.photos} label="Bilder" testidPrefix={`photo-${idx}`}
                onAdd={(files) => uploadPhotosFor(idx, files)}
                onRemove={(pi) => removePhotoAt(idx, pi)}
                onPreview={(u) => setLightbox(u)}
                onPickProgress={() => openProgressPicker(idx)}
                uploading={uploading} />
              <PhotoRow photos={s.signed_card_photo ? [s.signed_card_photo] : []} label="Signert kort (1 bilde)" testidPrefix={`card-${idx}`}
                onAdd={(files) => uploadCardFor(idx, files)}
                onRemove={() => updateScaffold(idx, { signed_card_photo: null })}
                onPreview={(u) => setLightbox(u)}
                uploading={uploading} />
            </div>
          ))}
          <button type="button" onClick={addScaffold}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white text-sm rounded-md font-semibold shadow-sm" style={{ color: "#fff" }} data-testid="add-scaffold-btn">
            <Plus className="w-4 h-4" /> Legg til stillas
          </button>
        </div>

        {/* Section 3: General notes + inspector signature */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Generelle merknader</label>
            <textarea value={form.general_notes} onChange={(e) => setForm({ ...form, general_notes: e.target.value })}
              placeholder="Andre observasjoner" className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" rows={3} data-testid="input-general-notes" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Inspektør signatur</label>
            <SignaturePad value={form.inspector_signature} onChange={(dataUri) => setForm({ ...form, inspector_signature: dataUri })} label="Signer her" />
          </div>
        </div>

        {/* Save button */}
        <button onClick={save} disabled={saving || !canSave() || uploading}
          className="w-full py-3 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 disabled:cursor-not-allowed text-white rounded-md text-sm font-bold shadow-md flex items-center justify-center gap-2"
          style={{ color: "#fff" }} data-testid="save-btn">
          {saving ? <><Loader2 className="w-5 h-5 animate-spin" /> Lagrer...</> : "Lagre og generer signeringslink"}
        </button>
      </fieldset>

      {/* Result modal */}
      {result && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Lagret!</h3>
                <p className="text-xs text-slate-500 font-mono">{result.code}</p>
              </div>
            </div>
            {[["Kundens signeringslink", `${window.location.origin}/apps/kontroll/sign/${result.sign_token}`, "sign"],
              ["Fortsett å redigere", `${window.location.origin}/apps/kontroll/edit/${result.edit_token}`, "edit"]].map(([label, url, k]) => (
              <div key={k} className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</div>
                <div className="flex gap-1">
                  <input readOnly value={url} className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded bg-slate-50 text-slate-700" data-testid={`link-${k}`} />
                  <button type="button" onClick={() => copyLink(url, k)}
                    className="px-2 py-1.5 bg-slate-900 rounded text-white" style={{ color: "#fff" }} data-testid={`copy-${k}`}>
                    {copied[k] ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <button type="button" onClick={() => { navigate(`/apps/kontroll/edit/${result.edit_token}`, { replace: true }); setResult(null); }}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-900 rounded-md text-sm hover:bg-slate-50">Rediger</button>
              <button type="button" onClick={() => window.open(`/apps/kontroll/sign/${result.sign_token}`, "_blank")}
                className="flex-1 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md text-sm font-medium" style={{ color: "#fff" }}>Åpne signeringslink →</button>
            </div>
            <button type="button"
              onClick={() => { setForm(emptyForm()); setResult(null); setCopied({ edit: false, sign: false }); navigate("/apps/kontroll", { replace: true }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="w-full mt-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md text-sm font-semibold flex items-center justify-center gap-2" style={{ color: "#fff" }} data-testid="start-new-btn">
              <Plus className="w-4 h-4" /> Start ny kontroll
            </button>
          </div>
        </div>
      )}

      {/* Progress-feed photo picker */}
      {progress.open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setProgress((p) => ({ ...p, open: false }))}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="font-bold text-slate-900">Velg bilder fra progresjon</h3>
              <button type="button" onClick={() => setProgress((p) => ({ ...p, open: false }))} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {progress.loading ? (
                <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : progress.photos.length === 0 ? (
                <p className="text-center text-sm text-slate-400 italic py-10">Ingen progresjonsbilder for dette prosjektet ennå.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {progress.photos.map((ph, i) => {
                    const on = progress.sel.has(ph.url);
                    return (
                      <button type="button" key={`${ph.url}-${i}`} onClick={() => toggleProgressSel(ph.url)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 ${on ? "border-cyan-500 ring-2 ring-cyan-300" : "border-slate-200"}`}
                        data-testid={`progress-photo-${i}`}>
                        <img src={ph.url} alt="" className="w-full h-full object-cover" />
                        {on && <span className="absolute top-1 right-1 w-5 h-5 bg-cyan-500 text-white rounded-full flex items-center justify-center"><Check className="w-3 h-3" /></span>}
                        {ph.date && <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[9px] px-1 py-0.5 truncate">{ph.date}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setProgress((p) => ({ ...p, open: false }))} className="px-4 py-2 text-sm border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50">Avbryt</button>
              <button type="button" onClick={addProgressSelected} disabled={progress.sel.size === 0}
                className="px-4 py-2 text-sm bg-cyan-600 text-white rounded-md font-semibold disabled:opacity-40" style={{ color: "#fff" }} data-testid="progress-add-selected">
                Legg til ({progress.sel.size})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import previous kontroll */}
      {importer.open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setImporter((s) => ({ ...s, open: false }))}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="font-bold text-slate-900">Importer fra tidligere kontroll</h3>
              <button type="button" onClick={() => setImporter((s) => ({ ...s, open: false }))} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="px-4 py-3 border-b border-slate-100">
              <input value={importer.q} onChange={(e) => setImporter((s) => ({ ...s, q: e.target.value }))}
                placeholder="Søk kode / adresse / kunde..." className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md text-slate-900" data-testid="import-search" />
              <p className="text-[11px] text-slate-400 mt-1.5">Stillasene kopieres. Kortbildene tas på nytt for den nye kontrollen.</p>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {importer.loading ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : (() => {
                const q = importer.q.toLowerCase().trim();
                const items = q ? importer.items.filter((it) => [it.code, it.address, it.customer].some((f) => (f || "").toLowerCase().includes(q))) : importer.items;
                if (items.length === 0) return <p className="text-center text-sm text-slate-400 italic py-10">Ingen kontroller funnet.</p>;
                return (
                  <div className="space-y-2">
                    {items.map((it) => (
                      <button type="button" key={it.kontroll_id} onClick={() => applyImport(it)}
                        className="w-full text-left px-3 py-2.5 rounded-lg border border-slate-200 hover:border-cyan-400 hover:bg-cyan-50" data-testid={`import-item-${it.kontroll_id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs bg-slate-900 text-white px-1.5 py-0.5 rounded">{it.code}</span>
                          <span className="font-semibold text-sm text-slate-900">{it.address || "—"}</span>
                          <span className="ml-auto text-[11px] text-cyan-700 font-semibold">{it.scaffold_count} stillas</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">{it.customer} · {it.inspection_date}</div>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded" />
        </div>
      )}
    </div>
  );
}
